import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sectorBySlug } from '../data/sectors';
import { CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import SiteHeader from '../components/SiteHeader';
import RainbowText from '../components/RainbowText';
import '../styles/RainbowText.css';
import '../styles/StrategicEnquiry.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITION_OPTIONS = [
  'Developer',
  'Local Authority',
  'Investor',
  'Sponsor',
  'Education',
  'SEND Service',
  'Architect',
  'Community Organisation',
  'Health & Wellbeing Partner',
  'Commercial Operator',
  'Other',
];

const ENQUIRY_TYPES = [
  'Pilot Site Opportunity',
  'Development Collaboration',
  'Strategic Alignment',
  'Sponsorship Opportunity',
  'Community Infrastructure',
  'SEND & Education Integration',
  'Local Authority Discussion',
  'Commercial Development',
  'Brand / ESG Alignment',
  'Funding Opportunity',
  'Research & Innovation',
  'Other',
];

const SITE_OPTIONS = [
  'Existing Site / Building',
  'New Development Opportunity',
  'Multi-Site Rollout Discussion',
  'Strategic Consultation',
  'Community Space Integration',
  'Early Stage Exploration',
];

const SCALE_OPTIONS = [
  'Local Pilot',
  'Single Site',
  'Multi-Site Potential',
  'Regional Discussion',
  'National Rollout Potential',
  'Exploratory Conversation',
];

const SECTOR_OPTIONS = [
  'Housing & Communities',
  'Transport & Mobility',
  'Aviation',
  'Health & Hospitality',
  'Retail & Leisure',
  'SEND',
  'Education',
  'Community Infrastructure',
  'Property Development',
  'Health & Wellbeing',
  'Family Services',
  'ESG & Social Impact',
  'Local Government',
  'Neurodiversity Support',
  'Commercial Real Estate',
];

const initialState = {
  organisationName: '',
  contactName: '',
  position: '',
  email: '',
  phone: '',
  website: '',
  enquiryTypes: [],
  siteInterest: '',
  visionAlignment: '',
  opportunityScale: '',
  message: '',
  sectors: [],
};

const StrategicEnquiry = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromSector = sectorBySlug(params.get('sector') || '');
  const [formData, setFormData] = useState(() => (
    fromSector ? { ...initialState, sectors: [fromSector.label] } : initialState
  ));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedContact, setSubmittedContact] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleField = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toggleMulti = (field, value) => {
    setFormData((p) => {
      const cur = p[field] || [];
      return {
        ...p,
        [field]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedMessage = (formData.message || '').trim();

    if (!formData.organisationName.trim()) {
      toast.error('Please tell us your organisation name.');
      return;
    }
    if (!formData.contactName.trim()) {
      toast.error('Please share your contact name.');
      return;
    }
    if (!formData.position) {
      toast.error('Please select your position / role.');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please share an email address so we can reply.');
      return;
    }
    if (trimmedMessage.length < 10) {
      toast.error('Please share a few details (at least 10 characters) about your enquiry.');
      return;
    }
    if (formData.enquiryTypes.length === 0) {
      toast.error('Please select at least one enquiry type.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        message: trimmedMessage,
        visionAlignment: (formData.visionAlignment || '').trim() || null,
        phone: (formData.phone || '').trim() || null,
        website: (formData.website || '').trim() || null,
        siteInterest: formData.siteInterest || null,
        opportunityScale: formData.opportunityScale || null,
      };
      await axios.post(`${API}/strategic-enquiries`, payload);
      setSubmittedContact(formData.contactName.trim().split(/\s+/)[0] || 'there');
      setIsSubmitted(true);
      setFormData(initialState);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Strategic enquiry submission failed:', err);
      }
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="strategic-page" data-testid="strategic-page">
      <SEO
        title="Strategic Enquiries"
        description="Submit a strategic enquiry to the N3URO WORLD® executive team — advisory, partnership and infrastructure alignment."
        path="/strategic-enquiry"
      />
      <SiteHeader currentPath="/strategic-enquiry" />

      {/* Hero */}
      <section className="strategic-hero">
        <span className="strategic-eyebrow">N3URO WORLD® · Strategic Alignment</span>
        <h1 className="strategic-hero-title"><RainbowText>Strategic Alignment</RainbowText></h1>
        <p className="strategic-hero-lede">
          A considered conversation, not a contact form. Tell us about your organisation,
          your remit, and where you see resonance with the N3URO WORLD® model — and
          we'll come back to you with a senior, contextual response.
        </p>
        <div className="strategic-hero-divider" aria-hidden="true" />
      </section>

      {/* Content */}
      <main className="strategic-main">
        {isSubmitted ? (
          <div
            className="strategic-success"
            role="status"
            aria-live="polite"
            data-testid="strategic-success-card"
          >
            <div className="strategic-success-icon-wrap">
              <CheckCircle2 className="strategic-success-icon" strokeWidth={1.5} />
            </div>
            <h2 className="strategic-success-title">Thank you, {submittedContact}.</h2>
            <p className="strategic-success-text">
              Your strategic enquiry has been received. A senior member of the
              N3URO WORLD® team will review it personally and respond
              <strong> within 2 business days</strong> from <a href="mailto:hello@n3uro.world" className="strategic-success-link"><strong>hello@n3uro.world</strong></a>.
            </p>
            <p className="strategic-success-sub">
              For anything time-sensitive in the meantime, write to{' '}
              <a href="mailto:hello@n3uro.world" className="strategic-success-link">hello@n3uro.world</a>.
            </p>
            <div className="strategic-success-actions">
              <button
                type="button"
                onClick={() => setIsSubmitted(false)}
                className="strategic-btn-ghost"
                data-testid="strategic-send-another"
              >
                Submit another enquiry
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="strategic-btn-primary"
                data-testid="strategic-back-home"
              >
                Return to homepage
              </button>
            </div>
          </div>
        ) : (
          <form className="strategic-form" onSubmit={handleSubmit} noValidate data-testid="strategic-form">
            {/* Section 1 — Organisation Details */}
            <section className="strategic-section" data-testid="section-organisation">
              <div className="strategic-section-head">
                <span className="strategic-section-number">01</span>
                <h2 className="strategic-section-title">Organisation Details</h2>
              </div>

              <div className="strategic-grid two">
                <div className="strategic-field">
                  <label htmlFor="organisationName">Organisation / Company Name *</label>
                  <input
                    id="organisationName"
                    name="organisationName"
                    type="text"
                    required
                    value={formData.organisationName}
                    onChange={handleField}
                    placeholder="e.g. Greater Manchester Combined Authority"
                    data-testid="field-organisation-name"
                  />
                </div>
                <div className="strategic-field">
                  <label htmlFor="contactName">Contact Name *</label>
                  <input
                    id="contactName"
                    name="contactName"
                    type="text"
                    required
                    value={formData.contactName}
                    onChange={handleField}
                    placeholder="Your full name"
                    data-testid="field-contact-name"
                  />
                </div>
              </div>

              <div className="strategic-grid two">
                <div className="strategic-field">
                  <label htmlFor="position">Position / Role *</label>
                  <select
                    id="position"
                    name="position"
                    required
                    value={formData.position}
                    onChange={handleField}
                    data-testid="field-position"
                  >
                    <option value="">Select your role…</option>
                    {POSITION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="strategic-field">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleField}
                    placeholder="you@organisation.com"
                    data-testid="field-email"
                  />
                </div>
              </div>

              <div className="strategic-grid two">
                <div className="strategic-field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleField}
                    placeholder="+44 …"
                    data-testid="field-phone"
                  />
                </div>
                <div className="strategic-field">
                  <label htmlFor="website">Website / Organisation URL</label>
                  <input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleField}
                    placeholder="https://"
                    data-testid="field-website"
                  />
                </div>
              </div>
            </section>

            {/* Section 2 — Enquiry Type */}
            <section className="strategic-section" data-testid="section-enquiry-type">
              <div className="strategic-section-head">
                <span className="strategic-section-number">02</span>
                <h2 className="strategic-section-title">Enquiry Type</h2>
                <p className="strategic-section-help">
                  Select all that apply — we use this to route your enquiry to the right person.
                </p>
              </div>
              <div className="strategic-chip-grid">
                {ENQUIRY_TYPES.map((t) => {
                  const active = formData.enquiryTypes.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      className={`strategic-chip ${active ? 'is-active' : ''}`}
                      onClick={() => toggleMulti('enquiryTypes', t)}
                      data-testid={`chip-enquiry-${t.replace(/\W+/g, '-').toLowerCase()}`}
                      aria-pressed={active}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Section 3 — Site / Infrastructure Interest */}
            <section className="strategic-section" data-testid="section-site-interest">
              <div className="strategic-section-head">
                <span className="strategic-section-number">03</span>
                <h2 className="strategic-section-title">Site / Infrastructure Interest</h2>
                <p className="strategic-section-help">
                  Are you enquiring about an existing site, a new development, or something else?
                </p>
              </div>
              <div className="strategic-radio-grid">
                {SITE_OPTIONS.map((opt) => {
                  const checked = formData.siteInterest === opt;
                  return (
                    <label
                      key={opt}
                      className={`strategic-radio ${checked ? 'is-active' : ''}`}
                      data-testid={`radio-site-${opt.replace(/\W+/g, '-').toLowerCase()}`}
                    >
                      <input
                        type="radio"
                        name="siteInterest"
                        value={opt}
                        checked={checked}
                        onChange={handleField}
                      />
                      <span className="strategic-radio-dot" aria-hidden="true" />
                      <span className="strategic-radio-label">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Section 4 — Vision Alignment */}
            <section className="strategic-section" data-testid="section-vision">
              <div className="strategic-section-head">
                <span className="strategic-section-number">04</span>
                <h2 className="strategic-section-title">Vision Alignment</h2>
                <p className="strategic-section-help">
                  What interests you most about the N3URO WORLD® model? Where do you
                  see resonance with your organisation's remit?
                </p>
              </div>
              <textarea
                id="visionAlignment"
                name="visionAlignment"
                rows={5}
                value={formData.visionAlignment}
                onChange={handleField}
                placeholder="e.g. We're exploring how neurodivergent family infrastructure could anchor a town-centre regeneration scheme. The integrated parent-regulation layer feels uniquely aligned with our…"
                data-testid="field-vision-alignment"
              />
            </section>

            {/* Section 5 — Capacity / Opportunity */}
            <section className="strategic-section" data-testid="section-scale">
              <div className="strategic-section-head">
                <span className="strategic-section-number">05</span>
                <h2 className="strategic-section-title">Estimated Scale of Opportunity</h2>
              </div>
              <div className="strategic-field">
                <select
                  id="opportunityScale"
                  name="opportunityScale"
                  value={formData.opportunityScale}
                  onChange={handleField}
                  data-testid="field-scale"
                >
                  <option value="">Select scale…</option>
                  {SCALE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </section>

            {/* Section 6 — Message */}
            <section className="strategic-section" data-testid="section-message">
              <div className="strategic-section-head">
                <span className="strategic-section-number">06</span>
                <h2 className="strategic-section-title">Tell us more</h2>
                <p className="strategic-section-help">
                  Anything else we should know — context, timelines, who's involved, what success
                  looks like for you.
                </p>
              </div>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                value={formData.message}
                onChange={handleField}
                placeholder="Share your enquiry in your own words…"
                data-testid="field-message"
              />
            </section>

            {/* Section 7 — Strategic Sectors */}
            <section className="strategic-section" data-testid="section-sectors">
              <div className="strategic-section-head">
                <span className="strategic-section-number">07</span>
                <h2 className="strategic-section-title">Strategic Sectors <span className="strategic-optional">— optional</span></h2>
                <p className="strategic-section-help">
                  Does your organisation currently operate within any of these areas?
                </p>
                {fromSector && (
                  <p className="strategic-section-help" data-testid="sector-prefill-note">
                    We&rsquo;ve pre-selected <strong>{fromSector.label}</strong> from the page you came from — change it any time.
                  </p>
                )}
              </div>
              <div className="strategic-chip-grid">
                {SECTOR_OPTIONS.map((t) => {
                  const active = formData.sectors.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      className={`strategic-chip ${active ? 'is-active' : ''}`}
                      onClick={() => toggleMulti('sectors', t)}
                      data-testid={`chip-sector-${t.replace(/\W+/g, '-').toLowerCase()}`}
                      aria-pressed={active}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Submit */}
            <div className="strategic-submit-row">
              <p className="strategic-submit-note">
                Submissions are reviewed personally by the N3URO WORLD® team. We respond within 2 business days.
              </p>
              <button
                type="submit"
                className="strategic-submit-btn"
                disabled={isSubmitting}
                data-testid="strategic-submit-btn"
              >
                {isSubmitting ? 'Sending…' : 'Start Strategic Conversation'}
              </button>
            </div>
          </form>
        )}
      </main>

      {/* Minimal footer */}
      <footer className="strategic-footer">
        <p>
          N3URO WORLD<sup>®</sup> · Company No. 16474717 · United Kingdom ·{' '}
          <a href="mailto:hello@n3uro.world">hello@n3uro.world</a>
        </p>
        <p className="site-footer-address" data-testid="footer-address">
          71-75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ
        </p>
        <p className="strategic-footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/terms">Terms</Link>
        </p>
      </footer>
    </div>
  );
};

export default StrategicEnquiry;
